import { create } from 'zustand';
import type { Lang } from '../i18n';
import type { AttemptResult } from '../levels/Evaluator';
import type { ApplyResultOutcome } from '../progression/progressModel';
import type { Transmission } from '../sim/types';
import { getLevel } from '../levels';
import { CARS } from '../sim/cars';

export type Screen =
  | 'menu'
  | 'levels'
  | 'theory'
  | 'settings'
  | 'controls'
  | 'intro'
  | 'game'
  | 'debrief';

export interface Settings {
  drivingSide: 'right' | 'left';
  units: 'kmh' | 'mph';
  audio: boolean;
  camera: 'chase' | 'cockpit';
}

export interface DebriefData {
  result: AttemptResult;
  outcome: ApplyResultOutcome;
}

interface AppState {
  screen: Screen;
  lang: Lang;
  settings: Settings;
  /** Itinerario visible en la lista de niveles (automático o manual). */
  activeTrack: Transmission;
  activeLevelId: string | null;
  /** Coche elegido para conducción libre (null → el del nivel). */
  selectedCarId: string | null;
  debrief: DebriefData | null;
  /** Cambia para forzar el remontaje del juego (reintentos). */
  gameNonce: number;
  setScreen: (s: Screen) => void;
  setLang: (l: Lang) => void;
  updateSettings: (s: Partial<Settings>) => void;
  setTrack: (t: Transmission) => void;
  openIntro: (levelId: string) => void;
  startLevel: () => void;
  restartLevel: () => void;
  selectCar: (carId: string | null) => void;
  showDebrief: (data: DebriefData) => void;
  exitToMenu: () => void;
}

const SETTINGS_KEY = 'autoacademy.settings.v1';

interface PersistedPrefs {
  lang: Lang;
  settings: Settings;
}

function loadPrefs(): PersistedPrefs {
  const fallback: PersistedPrefs = {
    lang: 'es',
    settings: { drivingSide: 'right', units: 'kmh', audio: true, camera: 'chase' },
  };
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<PersistedPrefs>;
    return {
      lang: parsed.lang === 'en' ? 'en' : 'es',
      settings: { ...fallback.settings, ...(parsed.settings ?? {}) },
    };
  } catch {
    return fallback;
  }
}

function savePrefs(state: Pick<AppState, 'lang' | 'settings'>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      SETTINGS_KEY,
      JSON.stringify({ lang: state.lang, settings: state.settings }),
    );
  } catch {
    /* almacenamiento lleno o bloqueado: ignorar */
  }
}

const prefs = loadPrefs();

export const useAppStore = create<AppState>((set, get) => ({
  screen: 'menu',
  lang: prefs.lang,
  settings: prefs.settings,
  activeTrack: 'auto',
  activeLevelId: null,
  selectedCarId: null,
  debrief: null,
  gameNonce: 0,
  setScreen: (screen) => set({ screen }),
  setTrack: (activeTrack) => set({ activeTrack }),
  setLang: (lang) => {
    set({ lang });
    savePrefs({ lang, settings: get().settings });
  },
  updateSettings: (partial) => {
    const settings = { ...get().settings, ...partial };
    set({ settings });
    savePrefs({ lang: get().lang, settings });
  },
  openIntro: (levelId) => {
    const level = getLevel(levelId);
    const prevCar = get().selectedCarId;
    const carOk =
      prevCar !== null && (CARS[prevCar]?.manual ? 'manual' : 'auto') === level.track;
    set({
      activeLevelId: levelId,
      screen: 'intro',
      activeTrack: level.track,
      selectedCarId: carOk ? prevCar : null,
    });
  },
  startLevel: () => set({ screen: 'game', debrief: null }),
  restartLevel: () => set({ screen: 'game', debrief: null, gameNonce: get().gameNonce + 1 }),
  selectCar: (selectedCarId) => set({ selectedCarId }),
  showDebrief: (debrief) => set({ debrief, screen: 'debrief' }),
  exitToMenu: () => set({ screen: 'menu', activeLevelId: null }),
}));
